import React from 'react';
import Modal from '../../components/modal';
import useI18n from '../../i18n/useI18n';
import { formatPeriodFieldLabel } from './periodLabelUtils';

const DateScenarioDetailModal = ({ isOpen, onClose, record, scenarioType }) => {
  const { t } = useI18n();

  if (!isOpen || !record) {
    return null;
  }

  const DetailStatCard = ({ icon, label, value, accent = 'primary' }) => (
    <div className={`detail-card detail-card--${accent}`}>
      <div className="detail-card__icon">
        <i className={`zmdi ${icon}`}></i>
      </div>
      <div className="detail-card__meta">
        <span className="detail-card__label">{label}</span>
        <strong className="detail-card__value">{value || '-'}</strong>
      </div>
    </div>
  );

  const renderPeriodRows = (includeAnnouncement = false) => {
    const periods = [record.period1, record.period2, record.period3].filter(Boolean);
    return (
      <div className="date-scenario-detail__section">
        <h6 className="text-uppercase m-b-15">{t('DATE_SCENARIO_DETAIL_PERIODS')}</h6>
        {periods.length === 0 && <p className="text-muted mb-0">-</p>}
        <div className="period-grid">
          {periods.map((period, index) => (
            <div className="period-card" key={`period-${index + 1}`}>
              <div className="period-card__header">
                <span>{t('DATE_SCENARIO_MODAL_PERIOD_LABEL', { index: index + 1 })}</span>
                <i className="zmdi zmdi-time"></i>
              </div>
              <div className="period-card__value">{period?.label || '-'}</div>
              {includeAnnouncement && (
                <div className="period-card__announcement">
                  <small>
                    {formatPeriodFieldLabel(
                      t,
                      'DATE_SCENARIO_MODAL_FIELD_PERIOD_ANNOUNCEMENT',
                      index + 1
                    )}
                  </small>
                  <strong>{period?.announcementLabel || '-'}</strong>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderGuideDetails = () => (
    <>
      <div className="date-scenario-detail__section detail-grid">
        <DetailStatCard
          icon="zmdi-calendar"
          label={t('DATE_SCENARIO_MODAL_FIELD_ACADEMIC_YEAR')}
          value={record.academicYear || '-'}
        />
        <DetailStatCard
          icon="zmdi-file-text"
          label={t('DATE_SCENARIO_TABLE_COLUMN_GUIDE_TYPE')}
          value={record.guideTypeLabel || '-'}
          accent="purple"
        />
        <DetailStatCard
          icon="zmdi-check-circle"
          label={t('DATE_SCENARIO_TABLE_COLUMN_STATUS')}
          value={record.statusLabel || '-'}
          accent="success"
        />
      </div>
      <div className="date-scenario-detail__section">
        <h6 className="text-uppercase m-b-15">{t('DATE_SCENARIO_MODAL_FIELD_DESCRIPTION')}</h6>
        <p className="mb-0">{record.description || '-'}</p>
      </div>
      <div className="date-scenario-detail__section link-grid">
        <div>
          <small className="text-muted d-block">{t('DATE_SCENARIO_MODAL_FIELD_FILE_URL')}</small>
          {record.fileUrl ? (
            <a href={record.fileUrl} target="_blank" rel="noopener noreferrer">
              {t('DATE_SCENARIO_TABLE_ACTION_OPEN_LINK')}
            </a>
          ) : (
            <span>-</span>
          )}
        </div>
        <div>
          <small className="text-muted d-block">{t('DATE_SCENARIO_MODAL_FIELD_EXTERNAL_URL')}</small>
          {record.externalUrl ? (
            <a href={record.externalUrl} target="_blank" rel="noopener noreferrer">
              {t('DATE_SCENARIO_TABLE_ACTION_OPEN_LINK')}
            </a>
          ) : (
            <span>-</span>
          )}
        </div>
      </div>
    </>
  );

  const renderExamDetails = () => (
    <>
      <div className="detail-grid">
        <DetailStatCard
          icon="zmdi-calendar"
          label={t('DATE_SCENARIO_TABLE_COLUMN_ACADEMIC_YEAR')}
          value={record.academicYear || '-'}
        />
        <DetailStatCard
          icon="zmdi-graduation-cap"
          label={t('EXAM_DATES_TABLE_COLUMN_PROGRAM')}
          value={record.programName || '-'}
          accent="purple"
        />
        <DetailStatCard
          icon="zmdi-label-alt"
          label={t('EXAM_DATES_TABLE_COLUMN_EXAM_TYPE')}
          value={record.examTypeLabel || '-'}
          accent="secondary"
        />
        <DetailStatCard
          icon="zmdi-check-circle"
          label={t('DATE_SCENARIO_TABLE_COLUMN_STATUS')}
          value={record.statusLabel || '-'}
          accent="success"
        />
      </div>
      <div className="detail-grid">
        <DetailStatCard
          icon="zmdi-alarm"
          label={t('EXAM_DATES_TABLE_COLUMN_EXAM_DATE')}
          value={record.examDateLabel || '-'}
        />
        <DetailStatCard
          icon="zmdi-calendar-check"
          label={t('EXAM_DATES_TABLE_COLUMN_REGISTRATION_DEADLINE')}
          value={record.registrationDeadlineLabel || '-'}
          accent="warning"
        />
        <DetailStatCard
          icon="zmdi-notifications"
          label={t('EXAM_DATES_TABLE_COLUMN_RESULT_ANNOUNCEMENT')}
          value={record.resultAnnouncementDateLabel || '-'}
          accent="info"
        />
      </div>
      <div className="date-scenario-detail__section">
        <h6 className="text-uppercase m-b-15">{t('DATE_SCENARIO_MODAL_FIELD_NOTE')}</h6>
        <p className="mb-0">{record.notes || '-'}</p>
      </div>
      <div className="detail-grid">
        <DetailStatCard
          icon="zmdi-time-restore"
          label={t('DATE_SCENARIO_DETAIL_CREATED_AT')}
          value={record.createdAtLabel || '-'}
          accent="neutral"
        />
        <DetailStatCard
          icon="zmdi-refresh"
          label={t('DATE_SCENARIO_DETAIL_UPDATED_AT')}
          value={record.updatedAtLabel || '-'}
          accent="neutral"
        />
      </div>
    </>
  );

  const renderContent = () => {
    if (scenarioType === 'preferenceGuides') {
      return renderGuideDetails();
    }

    if (scenarioType === 'preferenceDates') {
      return (
        <>
          {renderPeriodRows(true)}
          <div className="m-t-20">
            <h6 className="text-uppercase">{t('DATE_SCENARIO_MODAL_FIELD_NOTE')}</h6>
            <p>{record.notes || '-'}</p>
          </div>
        </>
      );
    }

    if (scenarioType === 'exam') {
      return renderExamDetails();
    }

    return (
      <>
        {renderPeriodRows(false)}
        <div className="date-scenario-detail__section">
          <h6 className="text-uppercase m-b-15">{t('DATE_SCENARIO_MODAL_FIELD_NOTE')}</h6>
          <p className="mb-0">{record.notes || '-'}</p>
        </div>
        <div className="detail-grid">
          <DetailStatCard
            icon="zmdi-time-restore"
            label={t('DATE_SCENARIO_DETAIL_CREATED_AT')}
            value={record.createdAtLabel || '-'}
            accent="neutral"
          />
          <DetailStatCard
            icon="zmdi-refresh"
            label={t('DATE_SCENARIO_DETAIL_UPDATED_AT')}
            value={record.updatedAtLabel || '-'}
            accent="neutral"
          />
        </div>
      </>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={record.title || t('DATE_SCENARIO_DETAIL_TITLE')}
      subtitle={t('DATE_SCENARIO_DETAIL_SUBTITLE', { academicYear: record.academicYear || '-' })}
      icon="zmdi-calendar-note"
      size="large"
    >
      <div className="date-scenario-detail">
        {renderContent()}
        <div className="text-right m-t-20">
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
            {t('COMMON_ACTIONS_CLOSE') || 'Kapat'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DateScenarioDetailModal;


